'use client';
import { useEffect, useState } from 'react';

export interface VisualViewportRect {
    /** Offset of the visible area from the top of the layout viewport, px */
    top: number;
    /** Height of the visible area, px */
    height: number;
}

/**
 * The visible part of the page while it is smaller than the layout viewport, otherwise null.
 *
 * iOS Safari doesn't shrink the layout viewport when the on-screen keyboard opens, only the visual
 * one, so a `position: fixed; inset: 0` overlay keeps its bottom behind the keyboard. Placing the
 * overlay at this rect keeps it in the visible area. Returns null when nothing is covered
 * (desktop, Android which resizes the page), so callers can leave their CSS alone.
 *
 * @param enabled - listen only while true (e.g. while a modal is open)
 */
export function useVisualViewportRect(enabled: boolean): VisualViewportRect | null {
    const [rect, setRect] = useState<VisualViewportRect | null>(null);

    useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!enabled || !vv) {
            setRect(null);
            return;
        }

        let frame = 0;
        const measure = () => {
            frame = 0;
            const covered = window.innerHeight - vv.height > 1;
            setRect((prev) => {
                if (!covered) return null;
                if (prev && prev.top === vv.offsetTop && prev.height === vv.height) return prev;
                return { top: vv.offsetTop, height: vv.height };
            });
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };

        measure();
        vv.addEventListener('resize', schedule);
        vv.addEventListener('scroll', schedule);

        return () => {
            vv.removeEventListener('resize', schedule);
            vv.removeEventListener('scroll', schedule);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [enabled]);

    return rect;
}
